import type { ProblemDetails } from '@proerp/shared';
import { configureFilePreview } from '@proerp/ui';

/**
 * Basis-URL der API. In Produktion liefert der API-Container die SPA selbst aus,
 * daher reicht der relative Pfad `/api`. Lokal reicht der Vite-Proxy (siehe vite.config).
 */
const BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

/**
 * Liefert das aktuelle Access-Token (Entra, ADR 0011) für den Authorization-Header.
 * Wird vom Auth-Layer gesetzt; im Dev-Modus (kein Entra) bleibt es null.
 */
let accessTokenProvider: () => Promise<string | null> = async () => null;

export function setAccessTokenProvider(provider: () => Promise<string | null>): void {
  accessTokenProvider = provider;
}

/** Fehler mit RFC-7807-Details (CLAUDE.md §8). Trägt Feld-Fehler für Formulare. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem?: ProblemDetails,
  ) {
    super(formatApiErrorMessage(status, problem));
    this.name = 'ApiError';
  }

  get fieldErrors(): Record<string, string[]> | undefined {
    return this.problem?.errors;
  }

  /**
   * Nutzerlesbare Begründung: RFC-7807-`detail` (z. B. die konkrete Konflikt-Meldung des
   * Servers) — sonst die generische `message` (Titel bzw. Fallback). Für Toasts gedacht.
   */
  get detailMessage(): string {
    return this.problem?.detail ?? this.message;
  }

  /** Konkrete Server-Meldung (RFC-7807 `detail`), z. B. „Dir fehlt die Berechtigung …". */
  get detail(): string | undefined {
    return this.problem?.detail;
  }

  /** Kurztitel des Fehlertyps (RFC-7807 `title`), z. B. „Forbidden". */
  get title(): string | undefined {
    return this.problem?.title;
  }

  /** Referenz-ID bei Serverfehlern (5xx) — zum Nachschlagen im Server-Log. */
  get traceId(): string | undefined {
    return this.problem?.traceId;
  }
}

/** Feld-Fehler als „feld: Meldung; …" zusammenfassen (für Toast/Anzeige). */
function summarizeFieldErrors(errors?: Record<string, string[]>): string | undefined {
  if (!errors) return undefined;
  const parts = Object.entries(errors)
    .map(([field, messages]) => (messages.length ? `${field}: ${messages.join(', ')}` : null))
    .filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join('; ') : undefined;
}

/**
 * Baut die aussagekräftigste Fehlermeldung aus den Problem-Details: bevorzugt
 * `detail` (konkret) vor `title` (generisch), hängt Feld-Fehler und — bei
 * Serverfehlern — die Referenz-ID an. So zeigt jeder `error.message`-Toast den
 * eigentlichen Grund statt nur „Forbidden".
 */
export function formatApiErrorMessage(status: number, problem?: ProblemDetails): string {
  if (!problem) {
    return `Anfrage fehlgeschlagen (HTTP ${status})`;
  }
  const parts: string[] = [];
  const primary = problem.detail?.trim() || problem.title?.trim();
  if (primary) parts.push(primary);

  // Feld-Fehler nur ergänzen, wenn sie nicht schon im `detail` stehen.
  if (!problem.detail) {
    const fields = summarizeFieldErrors(problem.errors);
    if (fields) parts.push(fields);
  }

  if (parts.length === 0) parts.push(`Anfrage fehlgeschlagen (HTTP ${status})`);
  if (problem.traceId) parts.push(`Referenz: ${problem.traceId}`);
  return parts.join(' — ');
}

/** Menschlich lesbarer Fehlertext für beliebige Fehlerwerte (Inline-Anzeigen). */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return 'Unbekannter Fehler.';
}

/** Schlanker, typisierter Fetch-Wrapper. Datenzugriff läuft NUR hierüber (CLAUDE.md §9). */
export async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await accessTokenProvider();
  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    let problem: ProblemDetails | undefined;
    try {
      problem = (await response.json()) as ProblemDetails;
    } catch {
      // Antwort ohne JSON-Body — Status reicht.
    }
    throw new ApiError(response.status, problem);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/**
 * Lädt eine geschützte Datei mit dem Bearer-Token und stößt einen Browser-Download
 * an (`<a download>`). Der Dateiname muss explizit gesetzt werden, da Blob-URLs
 * keine `Content-Disposition` tragen.
 */
export async function downloadAuthedFile(path: string, filename: string): Promise<void> {
  const token = await accessTokenProvider();
  const response = await fetch(`${BASE_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new ApiError(response.status);
  }
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Wie `downloadAuthedFile`, aber per POST mit JSON-Body — für Endpunkte, die eine
 * Datei aus Parametern im Body erzeugen (z. B. Reporting-Export CSV/XLSX/PDF).
 */
export async function downloadAuthedFilePost(path: string, filename: string, body: unknown): Promise<void> {
  const token = await accessTokenProvider();
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new ApiError(response.status);
  }
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Holt eine geschützte Datei mit Bearer-Token und liefert die **rohen Bytes**.
 *
 * Für PDFs der einzige gangbare Weg (ADR 0075): pdf.js bekommt die Bytes direkt,
 * NICHT eine `blob:`-URL. Eine Blob-URL müsste pdf.js selbst laden — das scheitert
 * in Safari aus dem Worker heraus und wird in Produktion zusätzlich vom CSP
 * (`connect-src 'self'` deckt `blob:` nicht ab) mit „Status 0" abgewiesen.
 */
export async function fetchAuthedBytes(path: string): Promise<Uint8Array> {
  const token = await accessTokenProvider();
  const response = await fetch(`${BASE_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new ApiError(response.status);
  }
  return new Uint8Array(await response.arrayBuffer());
}

/**
 * Holt eine geschützte Datei mit Bearer-Token und liefert eine Blob-Object-URL
 * (für Inline-Vorschau in `<img>`). Aufrufer muss sie mit `URL.revokeObjectURL`
 * wieder freigeben. **Nicht für PDFs** — dafür `fetchAuthedBytes` + `PdfViewer`.
 */
export async function fetchAuthedBlobUrl(path: string): Promise<string> {
  const token = await accessTokenProvider();
  const response = await fetch(`${BASE_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new ApiError(response.status);
  }
  return URL.createObjectURL(await response.blob());
}

/** Lädt Binärbytes per PUT hoch (Content-Proxy), mit Bearer-Token (CLAUDE.md §9). */
/**
 * Sendet JSON-Payload + Dateien in EINEM multipart-Request (Feld `payload` als
 * JSON-String, Dateien unter `files`). Der Browser setzt den Content-Type mit
 * Boundary selbst — daher hier bewusst KEIN Content-Type-Header.
 */
export async function postAuthedMultipart<T>(
  path: string,
  payload: unknown,
  files: File[],
): Promise<T> {
  const token = await accessTokenProvider();
  const form = new FormData();
  form.set('payload', JSON.stringify(payload));
  for (const file of files) form.append('files', file, file.name);
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    body: form,
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!response.ok) {
    let problem: ProblemDetails | undefined;
    try {
      problem = (await response.json()) as ProblemDetails;
    } catch {
      // kein JSON-Body
    }
    throw new ApiError(response.status, problem);
  }
  return (await response.json()) as T;
}

export async function putAuthedFile(path: string, file: File): Promise<void> {
  const token = await accessTokenProvider();
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    throw new ApiError(response.status);
  }
}

/**
 * Lädt eine Datei per POST hoch (Rohdaten im Body, Bearer-Token) und gibt die
 * geparste JSON-Antwort zurück — z. B. für Kommissionier-Fotos, deren Metadaten
 * als Query-Parameter im Pfad stehen.
 */
export async function postAuthedFile<T>(path: string, file: File): Promise<T> {
  const token = await accessTokenProvider();
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    let problem: ProblemDetails | undefined;
    try {
      problem = (await response.json()) as ProblemDetails;
    } catch {
      // kein JSON-Body
    }
    throw new ApiError(response.status, problem);
  }
  return (await response.json()) as T;
}

/** Lädt einen rohen Byte-Block (z. B. einen Datei-Chunk) per PUT hoch. */
export async function putAuthedBytes(path: string, body: Blob): Promise<void> {
  const token = await accessTokenProvider();
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'PUT',
    body,
    headers: {
      'Content-Type': 'application/octet-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    throw new ApiError(response.status);
  }
}

/**
 * Lädt alle Seiten einer Liste (für „Export alles"): blättert mit `pageSize`
 * durch, bis `meta.total` erreicht ist. Reihenfolge/Filter kommen vom Aufrufer.
 */
export async function collectAllPages<T>(
  fetchPage: (page: number, pageSize: number) => Promise<{ data: T[]; meta: { total: number } }>,
  pageSize = 500,
): Promise<T[]> {
  const first = await fetchPage(1, pageSize);
  const all = [...first.data];
  const pages = Math.ceil(first.meta.total / pageSize);
  for (let page = 2; page <= pages; page += 1) {
    const next = await fetchPage(page, pageSize);
    all.push(...next.data);
  }
  return all;
}

/**
 * Die Datei-Vorschau aus `@proerp/ui` bekommt hier ihren Zugang zur API
 * (ADR 0075). Einmal beim Laden des Moduls: `apiClient` steckt in jedem
 * Datenpfad des ERP, damit ist `openFilePreview()` überall verdrahtet, ohne
 * dass eine Liste oder ein Zeilenmenü einen Provider um sich braucht.
 */
configureFilePreview({
  loadBytes: fetchAuthedBytes,
  download: downloadAuthedFile,
  formatError: describeError,
});
